import {
  IconBuildingBank,
  IconFileInvoice,
  IconShieldCheck,
  IconBuildingStore,
  IconCertificate,
  IconUsersGroup,
  IconShip,
  IconAward,
  IconLock,
} from "@tabler/icons-react";

import type {
  BusinessTypeOption,
  ComplianceService,
  Country,
  CountryCode,
} from "../store/@types/portal";

export const COUNTRIES: Country[] = [
  {
    code: "NG",
    name: "Nigeria",
    flagEmoji: "🇳🇬",
    currencyCode: "NGN",
    currencySymbol: "₦",
  },
  {
    code: "GH",
    name: "Ghana",
    flagEmoji: "🇬🇭",
    currencyCode: "GHS",
    currencySymbol: "GH₵",
  },
  {
    code: "ZA",
    name: "South Africa",
    flagEmoji: "🇿🇦",
    currencyCode: "ZAR",
    currencySymbol: "R",
  },
  {
    code: "EG",
    name: "Egypt",
    flagEmoji: "🇪🇬",
    currencyCode: "EGP",
    currencySymbol: "E£",
  },
];

// Each country has its own set of registration/compliance services.
// Swap or extend these lists freely — every other part of the wizard
// (selection state, summary, pricing) reads from this single source.
export const SERVICES_BY_COUNTRY: Record<CountryCode, ComplianceService[]> = {
  NG: [
    {
      id: "cac",
      name: "Business Registration",
      description: "Corporate Affairs Commission (CAC) Registration",
      icon: IconBuildingBank,
    },
    {
      id: "firs",
      name: "Tax Registration",
      description: "Federal Inland Revenue Service TIN Registration",
      icon: IconFileInvoice,
    },
    {
      id: "pencom",
      name: "Pension Registration",
      description: "National Pension Commission (PenCom) Enrolment",
      icon: IconUsersGroup,
    },
    {
      id: "scuml",
      name: "SCUML Registration",
      description: "Special Control Unit Against Money Laundering",
      icon: IconLock,
    },
    {
      id: "premises",
      name: "Business Premises Permit",
      description: "State Business Premises Registration",
      icon: IconBuildingStore,
    },
  ],
  GH: [
    {
      id: "rgd",
      name: "Business Registration",
      description: "Registrar General's Department Incorporation",
      icon: IconBuildingBank,
    },
    {
      id: "gra",
      name: "Tax Registration",
      description: "Ghana Revenue Authority TIN Registration",
      icon: IconFileInvoice,
    },
    {
      id: "ssnit",
      name: "Social Insurance",
      description: "SSNIT Employer Registration",
      icon: IconShieldCheck,
    },
    {
      id: "gipc",
      name: "Investment License",
      description: "Ghana Investment Promotion Centre Registration",
      icon: IconCertificate,
    },
    {
      id: "gepa",
      name: "Export License",
      description: "Ghana Export Promotion Authority Registration",
      icon: IconShip,
    },
  ],
  ZA: [
    {
      id: "cipc",
      name: "Business Registration",
      description: "Companies and Intellectual Property Commission",
      icon: IconBuildingBank,
    },
    {
      id: "sars",
      name: "Tax Registration",
      description: "South African Revenue Service Registration",
      icon: IconFileInvoice,
    },
    {
      id: "uif",
      name: "Social Insurance",
      description: "Unemployment Insurance Fund Registration",
      icon: IconShieldCheck,
    },
    {
      id: "bbbee",
      name: "B-BBEE Certification",
      description: "Broad-Based Black Economic Empowerment Certificate",
      icon: IconAward,
    },
    {
      id: "investsa",
      name: "Investment License",
      description: "InvestSA Operating License",
      icon: IconCertificate,
    },
  ],
  EG: [
    {
      id: "gafi_reg",
      name: "Business Registration",
      description: "General Authority for Investment (GAFI) Registration",
      icon: IconBuildingBank,
    },
    {
      id: "tax_eg",
      name: "Tax Registration",
      description: "Egyptian Tax Authority Registration",
      icon: IconFileInvoice,
    },
    {
      id: "social_eg",
      name: "Social Insurance",
      description: "National Organization for Social Insurance",
      icon: IconShieldCheck,
    },
    {
      id: "commercial_registry",
      name: "Commercial Registry",
      description: "Ministry of Trade Commercial Registry",
      icon: IconBuildingStore,
    },
    {
      id: "investment_license",
      name: "Investment License",
      description: "GAFI Operating / Activity License",
      icon: IconCertificate,
    },
  ],
};

export const BUSINESS_TYPES_BY_COUNTRY: Record<
  CountryCode,
  BusinessTypeOption[]
> = {
  NG: [
    { id: "private_limited", name: "Private Limited Company" },
    { id: "business_name", name: "Business Name (Sole Proprietorship)" },
    { id: "public_limited", name: "Public Limited Company" },
    { id: "ngo", name: "Incorporated Trustees (NGO)" },
  ],
  GH: [
    { id: "private_limited", name: "Private Limited Company" },
    { id: "sole_proprietorship", name: "Sole Proprietorship" },
    { id: "partnership", name: "Partnership" },
    { id: "external_company", name: "External Company (Branch)" },
  ],
  ZA: [
    { id: "pty_ltd", name: "Private Company (Pty Ltd)" },
    { id: "sole_proprietor", name: "Sole Proprietor" },
    { id: "npc", name: "Non-Profit Company (NPC)" },
    { id: "external_company", name: "External Company (Branch)" },
  ],
  EG: [
    { id: "llc", name: "Limited Liability Company" },
    { id: "joint_stock", name: "Joint Stock Company" },
    { id: "branch", name: "Branch of a Foreign Company" },
    { id: "sole_proprietorship", name: "Sole Proprietorship" },
  ],
};

// One flat fee per country, covering however many services are selected.
export const FLAT_FEE_USD_BY_COUNTRY: Record<CountryCode, number> = {
  NG: 1000,
  GH: 850,
  ZA: 1200,
  EG: 1000,
};

// Rough FX rates, used only to show an approximate local-currency figure.
export const APPROX_FX_RATE_TO_USD: Record<CountryCode, number> = {
  NG: 1600,
  GH: 15,
  ZA: 18,
  EG: 49,
};

export const SUGGESTED_QUESTIONS_BY_COUNTRY: Record<CountryCode, string[]> = {
  NG: [
    "What are the requirements for CAC registration?",
    "How do I comply with NUPRC regulations?",
    "What are the annual compliance requirements?",
    "How much is the minimum share capital?",
  ],
  GH: [
    "What documents does the RGD require?",
    "How do I register with SSNIT?",
    "What are Ghana's annual filing deadlines?",
    "What is the minimum capital for GIPC registration?",
  ],
  ZA: [
    "What does CIPC registration require?",
    "How do I get a B-BBEE certificate?",
    "What are SARS's annual compliance requirements?",
    "Do I need a UIF reference number?",
  ],
  EG: [
    "What are the requirements for GAFI registration?",
    "How do I comply with tax authority filings?",
    "What are the annual compliance requirements?",
    "How much is the minimum share capital?",
  ],
};
